import { useState, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  Settings, Download, Upload, Info, Lock,
  Loader2, Check, AlertCircle, X, Database,
  Mail, FileText, Save, RefreshCw, Sparkles, Users
} from 'lucide-react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface EmailTemplate {
  id?: string
  tipo: string
  oggetto: string
  corpo: string
}

const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    tipo: 'richiesta_documenti_piva',
    oggetto: 'Richiesta Documenti Partita IVA - {{nome}}',
    corpo: `Gentile {{nome}},\n\ncon riferimento al colloquio sostenuto, Le chiediamo cortesemente di inviarci i seguenti documenti necessari per l'attivazione della collaborazione in regime di Partita IVA:\n\n- Copia documento d'identità in corso di validità\n- Codice Fiscale\n- Visura camerale o certificato attribuzione P.IVA\n- Certificato di iscrizione all'Albo professionale (OPI)\n- Polizza assicurativa professionale in corso di validità\n- Curriculum Vitae aggiornato\n- DURC (Documento Unico di Regolarità Contributiva)\n\nLa preghiamo di inviare il tutto rispondendo a questa email.\n\nCordiali saluti,\nQuiCare Srl`,
  },
  {
    tipo: 'richiesta_documenti_ritenuta',
    oggetto: "Richiesta Documenti Ritenuta d'Acconto - {{nome}}",
    corpo: `Gentile {{nome}},\n\ncon riferimento al colloquio sostenuto, Le chiediamo cortesemente di inviarci i seguenti documenti necessari per l'attivazione della collaborazione in regime di Ritenuta d'Acconto:\n\n- Copia documento d'identità in corso di validità\n- Codice Fiscale\n- Certificato di iscrizione all'Albo professionale (OPI)\n- Polizza assicurativa professionale in corso di validità\n- Curriculum Vitae aggiornato\n- Autocertificazione di prestazione occasionale\n\nLa preghiamo di inviare il tutto rispondendo a questa email.\n\nCordiali saluti,\nQuiCare Srl`,
  },
  {
    tipo: 'invio_contratto',
    oggetto: 'Invio Contratto di Collaborazione - {{nome}}',
    corpo: `Gentile {{nome}},\n\nin allegato trova il contratto di collaborazione professionale.\n\nLa preghiamo di:\n1. Leggere attentamente tutte le clausole\n2. Firmare in ogni pagina dove indicato\n3. Restituire copia firmata rispondendo a questa email\n\nPer qualsiasi chiarimento non esiti a contattarci.\n\nCordiali saluti,\nQuiCare Srl`,
  },
]

const TEMPLATE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  richiesta_documenti_piva: { label: 'Richiesta Doc. P.IVA', icon: '📋', color: 'from-orange-500 to-amber-600' },
  richiesta_documenti_ritenuta: { label: "Richiesta Doc. Ritenuta", icon: '📄', color: 'from-yellow-500 to-orange-600' },
  invio_contratto: { label: 'Invio Contratto', icon: '📝', color: 'from-indigo-500 to-purple-600' },
}

export function SettingsPage() {
  const { isSuperAdmin, user } = useAuth()
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPw, setChangingPw] = useState(false)

  // Email templates
  const [templates, setTemplates] = useState<EmailTemplate[]>(DEFAULT_TEMPLATES)
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [savingTemplate, setSavingTemplate] = useState('')
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null)

  // CC email settings
  const [ccEmails, setCcEmails] = useState('')
  const [savingCc, setSavingCc] = useState(false)

  // API Key Settings
  const [geminiApiKey, setGeminiApiKey] = useState('')

  useEffect(() => {
    fetchTemplates()
    // Load local settings
    const savedCc = localStorage.getItem('quicare_cc_emails')
    if (savedCc) setCcEmails(savedCc)
    const savedGemini = localStorage.getItem('GEMINI_API_KEY')
    if (savedGemini) setGeminiApiKey(savedGemini)
  }, [])

  async function fetchTemplates() {
    setLoadingTemplates(true)
    const { data, error } = await supabase.from('email_templates').select('*')
    if (!error && data && data.length > 0) {
      const merged = DEFAULT_TEMPLATES.map(dt => {
        const found = data.find((d: any) => d.tipo === dt.tipo)
        return found ? { ...dt, ...found } : dt
      })
      setTemplates(merged)
    }
    setLoadingTemplates(false)
  }

  async function handleSaveTemplate(template: EmailTemplate) {
    setSavingTemplate(template.tipo)
    try {
      const { error } = await supabase.from('email_templates').upsert({
        tipo: template.tipo,
        oggetto: template.oggetto,
        corpo: template.corpo,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'tipo' })
      if (error) throw error
      setMsg({ type: 'ok', text: `Template "${TEMPLATE_LABELS[template.tipo]?.label}" salvato!` })
      setEditingTemplate(null)
    } catch (err: any) {
      setMsg({ type: 'err', text: err.message || 'Errore salvataggio' })
    } finally {
      setSavingTemplate('')
    }
  }

  function handleResetTemplate(tipo: string) {
    const def = DEFAULT_TEMPLATES.find(t => t.tipo === tipo)
    if (def) {
      setTemplates(prev => prev.map(t => t.tipo === tipo ? { ...t, oggetto: def.oggetto, corpo: def.corpo } : t))
    }
  }

  // Export JSON
  async function handleExportJSON() {
    setExporting(true)
    setMsg(null)
    try {
      const { data: nurses, error: e1 } = await supabase.from('nurses').select('*')
      const { data: interviews, error: e2 } = await supabase.from('interviews').select('*')
      const { data: accesses, error: e3 } = await supabase.from('accesses').select('*')
      const { data: docs, error: e4 } = await supabase.from('documents').select('*')
      if (e1 || e2) throw new Error(e1?.message || e2?.message || 'Errore export')

      const blob = new Blob([JSON.stringify({
        nurses: nurses || [], interviews: interviews || [],
        accesses: accesses || [], documents: docs || [],
        exported_at: new Date().toISOString(),
      }, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `gestione-infermieri-backup-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      setMsg({ type: 'ok', text: 'Backup completo esportato!' })
    } catch (err: any) {
      setMsg({ type: 'err', text: err.message || 'Errore export' })
    } finally {
      setExporting(false)
    }
  }

  function camelToSnake(obj: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      result[snakeKey] = (value === '' || value === undefined) ? null : value
    }
    return result
  }

  async function handleImportJSON(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    setMsg(null)
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      if (!confirm('Questo cancellerà tutti i dati esistenti. Continuare?')) { setImporting(false); return }

      let nursesCount = 0, interviewsCount = 0
      await supabase.from('interviews').delete().neq('id', '00000000-0000-0000-0000-000000000000')
      await supabase.from('nurses').delete().neq('id', '00000000-0000-0000-0000-000000000000')

      if (data.nurses?.length) {
        const clean = data.nurses.map((n: any) => { const { id, created_at, updated_at, ...rest } = camelToSnake(n); return rest })
        const { error } = await supabase.from('nurses').insert(clean)
        if (error) throw new Error('Errore nurses: ' + error.message)
        nursesCount = clean.length
      }
      if (data.interviews?.length) {
        const clean = data.interviews.map((i: any) => { const { id, created_at, updated_at, ...rest } = camelToSnake(i); return rest })
        const { error } = await supabase.from('interviews').insert(clean)
        if (error) throw new Error('Errore interviews: ' + error.message)
        interviewsCount = clean.length
      }
      if (Array.isArray(data) && data.length > 0) {
        const clean = data.map((n: any) => { const { id, created_at, updated_at, ...rest } = camelToSnake(n); return rest })
        const { error } = await supabase.from('nurses').insert(clean)
        if (error) throw new Error('Errore: ' + error.message)
        nursesCount = clean.length
      }
      setMsg({ type: 'ok', text: `Importati ${nursesCount} infermieri e ${interviewsCount} colloqui.` })
    } catch (err: any) {
      setMsg({ type: 'err', text: err.message || 'Errore JSON' })
    } finally {
      setImporting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleChangePassword() {
    if (!newPassword || newPassword.length < 6) { setMsg({ type: 'err', text: 'Min 6 caratteri' }); return }
    if (newPassword !== confirmPassword) { setMsg({ type: 'err', text: 'Le password non corrispondono' }); return }
    setChangingPw(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setMsg({ type: 'ok', text: 'Password aggiornata!' })
      setNewPassword(''); setConfirmPassword('')
    } catch (err: any) {
      setMsg({ type: 'err', text: err.message })
    } finally { setChangingPw(false) }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl shadow-lg shadow-slate-500/20">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-800">Impostazioni</h2>
            <p className="text-sm text-slate-400">Gestione profilo, template email e configurazione</p>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`p-4 rounded-xl flex items-center gap-2 text-sm animate-fade-in ${msg.type === 'ok' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {msg.type === 'ok' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* CC Email Settings */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" /> Destinatari CC Email
          <span className="ml-auto text-xs text-slate-400 font-normal">Indirizzi email aggiunti automaticamente in CC</span>
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Indirizzi Email CC (separati da virgola)</label>
            <input
              type="text"
              value={ccEmails}
              onChange={e => setCcEmails(e.target.value)}
              placeholder="es: carlo.vergara@quicare.it, altro@email.com"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => {
                localStorage.setItem('quicare_cc_emails', ccEmails)
                setMsg({ type: 'ok', text: 'Indirizzi CC salvati!' })
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
            >
              <Save className="w-4 h-4" /> Salva CC
            </button>
          </div>
        </div>
      </div>

      {/* Gemini API Key Settings */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <span className="text-emerald-500">✨</span> A.I. Gemini Configuration
          <span className="ml-auto text-xs text-slate-400 font-normal">Necessaria per OCR e Autocompilazione Intelligente</span>
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Gemini API Key</label>
             <input
              type="password"
              value={geminiApiKey}
              onChange={e => setGeminiApiKey(e.target.value)}
              placeholder="Incolla qui la tua API Key di Google AI Studio..."
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-[10px] text-slate-400 mt-1">La chiave viene salvata solo localmente in questo browser e non viene condivisa con QuiCare.</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => {
                localStorage.setItem('GEMINI_API_KEY', geminiApiKey.trim())
                setMsg({ type: 'ok', text: 'Gemini API Key salvata con successo!' })
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
            >
              <Save className="w-4 h-4" /> Salva API Key
            </button>
          </div>
        </div>
      </div>

      {/* Email Templates */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-500" /> Template Email
          <span className="ml-auto text-xs text-slate-400 font-normal">Usa {"{{nome}}"} per il nome del professionista</span>
        </h3>

        {loadingTemplates ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento template...
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(template => {
              const info = TEMPLATE_LABELS[template.tipo] || { label: template.tipo, icon: '📧', color: 'from-slate-500 to-slate-600' }
              const isEditing = editingTemplate === template.tipo
              return (
                <div key={template.tipo} className="border border-slate-200 rounded-xl overflow-hidden transition-all hover:shadow-md">
                  {/* Template header */}
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setEditingTemplate(isEditing ? null : template.tipo)}
                  >
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${info.color} flex items-center justify-center text-base shadow-md`}>
                      {info.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-800">{info.label}</div>
                      <div className="text-xs text-slate-400 truncate">{template.oggetto}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {savingTemplate === template.tipo ? (
                        <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                      ) : (
                        <FileText className={`w-4 h-4 transition-transform ${isEditing ? 'rotate-45 text-indigo-500' : 'text-slate-300'}`} />
                      )}
                    </div>
                  </div>

                  {/* Template editor */}
                  {isEditing && (
                    <div className="border-t border-slate-200 p-4 bg-slate-50/50 animate-fade-in space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Oggetto</label>
                        <input
                          type="text"
                          value={template.oggetto}
                          onChange={e => setTemplates(prev => prev.map(t => t.tipo === template.tipo ? { ...t, oggetto: e.target.value } : t))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Corpo Email</label>
                        <ReactQuill
                          value={template.corpo}
                          onChange={val => setTemplates(prev => prev.map(t => t.tipo === template.tipo ? { ...t, corpo: val } : t))}
                          theme="snow"
                          className="bg-white rounded-xl overflow-hidden"
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['clean']
                            ]
                          }}
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <button
                          onClick={() => handleResetTemplate(template.tipo)}
                          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Ripristina default
                        </button>
                        <button
                          onClick={() => handleSaveTemplate(template)}
                          disabled={savingTemplate === template.tipo}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" /> Salva Template
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-purple-500" /> Cambia Password
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Nuova Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 caratteri"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Conferma Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Ripeti la password"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={handleChangePassword} disabled={changingPw || !newPassword}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm disabled:opacity-50">
            {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {changingPw ? 'Aggiornamento...' : 'Aggiorna Password'}
          </button>
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-500" /> Backup e Ripristino Dati
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={handleExportJSON} disabled={exporting}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl hover:shadow-md transition-all group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
              {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            </div>
            <div className="text-left">
              <div className="text-sm font-bold text-slate-800">Esporta Backup</div>
              <div className="text-xs text-slate-500">Tutti i dati in JSON</div>
            </div>
          </button>
          {isSuperAdmin && (
            <label className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl hover:shadow-md transition-all cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                {importing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-slate-800">Importa da JSON</div>
                <div className="text-xs text-slate-500">Carica dati da file</div>
              </div>
              <input ref={fileRef} type="file" accept=".json" onChange={handleImportJSON} className="hidden" disabled={importing} />
            </label>
          )}
        </div>
      </div>

      {/* App Info */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" /> Informazioni App
        </h3>
        <div className="space-y-2 text-sm text-slate-600">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-400">Versione</span>
            <span className="font-bold">2.0.0 <span className="text-xs text-indigo-500 ml-1">Premium</span></span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-400">Utente</span>
            <span className="font-bold">{user?.displayName}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-400">Email</span>
            <span className="font-bold">{user?.email}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-400">Ruolo</span>
            <span className="font-bold capitalize">{user?.role?.replace(/_/g, ' ')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
